import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, Copy, Pencil, Check, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => { try { return format(parseISO(iso), "d MMM yyyy HH:mm"); } catch { return iso; } };

export const LeagueRestricted = ({ league }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/leagues/${league.id}/restricted`); setData(r.data); return r.data; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load"); }
  };
  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try { await api.put(`/leagues/${league.id}/restricted`, edit); toast.success("Restricted info saved"); setEdit(null); setData(null); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async (v, label) => { try { await navigator.clipboard.writeText(v || ""); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); } };

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid={`league-restricted-${league.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] tracking-widest uppercase text-amber-400 inline-flex items-center gap-1.5 hover:opacity-80" data-testid={`league-restricted-toggle-${league.id}`}>
        <Lock className="w-3 h-3" /> Restricted info · admins & club secretary {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/[0.04] p-3 text-sm">
          {!data ? <div className="opacity-50 text-xs">Loading…</div> : edit ? (
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Website login URL</Label><Input value={edit.login_url || ""} onChange={e => setEdit({ ...edit, login_url: e.target.value })} className="mt-1" placeholder="https://…" data-testid="restricted-login-url" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Username / email</Label><Input value={edit.username || ""} onChange={e => setEdit({ ...edit, username: e.target.value })} className="mt-1" data-testid="restricted-username" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Password</Label><Input value={edit.password || ""} onChange={e => setEdit({ ...edit, password: e.target.value })} className="mt-1" data-testid="restricted-password" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Private notes</Label><Textarea rows={2} value={edit.notes || ""} onChange={e => setEdit({ ...edit, notes: e.target.value })} className="mt-1" data-testid="restricted-notes" /></div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={() => setEdit(null)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border">Cancel</button>
                <button type="button" onClick={save} disabled={busy} className="btn-volt px-4 py-2 rounded-md text-[10px] tracking-widest uppercase inline-flex items-center gap-1 disabled:opacity-40" data-testid="restricted-save"><Check className="w-3 h-3" /> {busy ? "Saving…" : "Save"}</button>
              </div>
            </div>
          ) : (
            <>
              {!data.has_data && <div className="text-xs opacity-60" data-testid="restricted-empty">Nothing stored yet.</div>}
              {data.has_data && (
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                  <Row label="Website login" value={data.login_url} link onCopy={copy} />
                  <Row label="Username" value={data.username} onCopy={copy} />
                  <Row label="Password" value={data.password} secret show={show} onToggle={() => setShow(s => !s)} onCopy={copy} />
                  {data.notes && <div className="md:col-span-2"><div className="text-[10px] tracking-widest uppercase opacity-50">Notes</div><div className="whitespace-pre-line opacity-90">{data.notes}</div></div>}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                <span className="text-[10px] opacity-50">{data.updated_at ? `Updated ${fmt(data.updated_at)}${data.updated_by_name ? ` by ${data.updated_by_name}` : ""} · every view is logged` : "Encrypted at rest · every view is logged"}</span>
                <button type="button" onClick={() => setEdit({ login_url: data.login_url || "", username: data.username || "", password: data.password || "", notes: data.notes || "" })} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-amber-400 hover:text-amber-400 inline-flex items-center gap-1" data-testid="restricted-edit"><Pencil className="w-3 h-3" /> {data.has_data ? "Edit" : "Add details"}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, secret, show, onToggle, link, onCopy }) => (
  <div>
    <div className="text-[10px] tracking-widest uppercase opacity-50">{label}</div>
    <div className="flex items-center gap-2">
      {!value ? <span className="opacity-40">—</span> : link ? <a href={value} target="_blank" rel="noreferrer" className="hover:text-[#83B68B] truncate inline-flex items-center gap-1">{value}<ExternalLink className="w-3 h-3" /></a>
        : <span className="font-mono" data-testid={`restricted-value-${label.toLowerCase()}`}>{secret && !show ? "••••••••" : value}</span>}
      {value && secret && <button type="button" onClick={onToggle} className="opacity-60 hover:opacity-100" title={show ? "Hide" : "Show"} data-testid="restricted-reveal">{show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>}
      {value && <button type="button" onClick={() => onCopy(value, label)} className="opacity-60 hover:opacity-100" title="Copy"><Copy className="w-3 h-3" /></button>}
    </div>
  </div>
);
